/**
 * @description Commission related tab. Shows all agent privileges with commission value in rupees or percentage
 */
import React, { useEffect, useState } from "react";
import { Button, Row, Col, Divider, Typography, InputNumber, Radio, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import './render-tabs.style.scss';
import { isValidArray } from "../../../utils/utilities";
import { getScheduleCommissionAction, updateScheduleCommissionAction } from "../../../actions/admin.action";
import { getAgentPrivileges } from "../../../actions/agents.actions";
import { agentPrivilegesSelector } from "../../../selector/agents.selector";

const { Text } = Typography;

export const COMMISSION_TYPE_FLAT = "FLAT";
export const COMMISSION_TYPE_PERCENTAGE = "PERCENTAGE";

const isValueSet = (entry) => entry && entry.value !== null && entry.value !== undefined && entry.value !== '';

const commissionFormatter = (type) => (value) => {
    if(value === null || value === undefined || value === ''){
        return '';
    }
    return type === COMMISSION_TYPE_PERCENTAGE ? `${value} %` : `₹ ${value}`;
};

const commissionParser = (value) => value ? value.replace(/[^\d.]/g, '') : '';

export const ScheduleCommission = (props) => {

    const{
        scheduleData,
        isCommissionOpened
    } = props;

    const dispatch = useDispatch();
    const agentPrivileges = useSelector(agentPrivilegesSelector);
    const [commission, setCommission] = useState({});
    const [savedCommission, setSavedCommission] = useState({});
    const [quickValue, setQuickValue] = useState(null);
    const [quickType, setQuickType] = useState(COMMISSION_TYPE_FLAT);

    useEffect(()=>{
        if(isCommissionOpened && scheduleData){
            getScheduleCommissionAction(scheduleData.id,(data)=>{
                let loaded = data?data:{};
                setCommission(loaded);
                setSavedCommission(loaded);
            });
            if(!isValidArray(agentPrivileges)){
                dispatch(getAgentPrivileges());
            }
        }
    },[isCommissionOpened,scheduleData]);

    const isDirty = JSON.stringify(commission) !== JSON.stringify(savedCommission);
    const setCount = isValidArray(agentPrivileges)?agentPrivileges.filter((element)=>isValueSet(commission[element.privilege])).length:0;

    const onValueChange = (privilege, value) => {
        setCommission((prev)=>{
            let entry = prev[privilege]?prev[privilege]:{};
            return {...prev, [privilege]:{...entry, value:value, type:entry.type?entry.type:COMMISSION_TYPE_FLAT}};
        });
    };

    const onTypeChange = (privilege, type) => {
        setCommission((prev)=>{
            let entry = prev[privilege]?prev[privilege]:{};
            return {...prev, [privilege]:{...entry, type:type}};
        });
    };

    const clearRow = (privilege) => {
        setCommission((prev)=>{
            let updated = {...prev};
            delete updated[privilege];
            return updated;
        });
    };

    const applyToAll = () => {
        if(quickValue === null || quickValue === undefined || quickValue === ''){
            message.error("Enter a value to apply to all privileges");
            return;
        }
        if(quickType === COMMISSION_TYPE_PERCENTAGE && quickValue > 100){
            message.error("Percentage cannot be more than 100");
            return;
        }
        let updated = {};
        agentPrivileges.forEach((element)=>{
            updated[element.privilege] = { value: quickValue, type: quickType };
        });
        setCommission(updated);
    };

    const clearAll = () => {
        setCommission({});
    };

    const handleUpdate = () => {
        let formattedCommission = {};
        let isInvalid = false;
        agentPrivileges.forEach((element) => {
            let entry = commission[element.privilege];
            if(isValueSet(entry)){
                let type = entry.type?entry.type:COMMISSION_TYPE_FLAT;
                if(entry.value < 0 || (type === COMMISSION_TYPE_PERCENTAGE && entry.value > 100)){
                    message.error(`Invalid commission for ${element.privilege}. Percentage must be between 0 and 100`);
                    isInvalid = true;
                    return;
                }
                formattedCommission[element.privilege] = {
                    value: entry.value,
                    type: type
                };
            }
        });
        if(!isInvalid){
            updateScheduleCommissionAction(scheduleData.id, formattedCommission, ()=>{
                setCommission(formattedCommission);
                setSavedCommission(formattedCommission);
            });
        }
    };

    const renderTypeToggle = (value, onChange) => (
        <Radio.Group
            optionType="button"
            buttonStyle="solid"
            size="small"
            value={value}
            onChange={(e)=>{onChange(e.target.value);}}
        >
            <Radio.Button value={COMMISSION_TYPE_FLAT}>&#8377;</Radio.Button>
            <Radio.Button value={COMMISSION_TYPE_PERCENTAGE}>%</Radio.Button>
        </Radio.Group>
    );

    return(
        <div className="extend-block-root">
            <div className="schedule-commission-root">
                <Row align="middle" justify="space-between">
                    <Col>
                        <Text strong style={{color: '#1057B9'}}>Commission by privilege</Text>
                        <Text className="commission-count-badge">
                            {setCount} of {isValidArray(agentPrivileges)?agentPrivileges.length:0} set
                        </Text>
                    </Col>
                    <Col>
                        <Text type="secondary" style={{fontSize:13}}>Blank rows are not saved</Text>
                    </Col>
                </Row>
                <Divider style={{margin:'12px 0'}}/>
                <div className="commission-quick-apply">
                    <Row gutter={[8,8]} align="middle">
                        <Col><Text style={{fontSize:13, color:'#595959'}}>Quick apply</Text></Col>
                        <Col>
                            <InputNumber
                                min={0}
                                max={quickType === COMMISSION_TYPE_PERCENTAGE?100:undefined}
                                size="small"
                                placeholder="Value"
                                value={quickValue}
                                formatter={commissionFormatter(quickType)}
                                parser={commissionParser}
                                style={{width:90}}
                                onChange={(value)=>{setQuickValue(value);}}
                            />
                        </Col>
                        <Col>{renderTypeToggle(quickType, setQuickType)}</Col>
                        <Col><Button className="commission-plain-btn" onClick={applyToAll}>Apply to all</Button></Col>
                        <Col><Button className="commission-plain-btn commission-danger-btn" onClick={clearAll}>Clear all</Button></Col>
                    </Row>
                </div>
                <Row className="commission-header-row" style={{marginTop:12}}>
                    <Col span={10}><Text style={{fontSize:12, color:'#8c8c8c'}}>Privilege</Text></Col>
                    <Col span={5}><Text style={{fontSize:12, color:'#8c8c8c'}}>Type</Text></Col>
                    <Col span={7}><Text style={{fontSize:12, color:'#8c8c8c'}}>Commission</Text></Col>
                    <Col span={2}></Col>
                </Row>
                {isValidArray(agentPrivileges)&&agentPrivileges.map((element)=>{
                    let entry = commission[element.privilege]?commission[element.privilege]:{};
                    let rowSet = isValueSet(entry);
                    return(
                        <Row key={element.privilege} align="middle" className="commission-row">
                            <Col span={10}>
                                <Text style={rowSet?{fontWeight:500}:{color:'#595959'}}>{element.privilege}</Text>
                            </Col>
                            <Col span={5}>
                                {renderTypeToggle(entry.type?entry.type:COMMISSION_TYPE_FLAT, (type)=>{onTypeChange(element.privilege, type);})}
                            </Col>
                            <Col span={7}>
                                <InputNumber
                                    min={0}
                                    max={entry.type === COMMISSION_TYPE_PERCENTAGE?100:undefined}
                                    value={entry.value !== undefined?entry.value:null}
                                    placeholder="No commission"
                                    formatter={commissionFormatter(entry.type?entry.type:COMMISSION_TYPE_FLAT)}
                                    parser={commissionParser}
                                    style={rowSet?{width:'90%', borderColor:'#1057B9'}:{width:'90%'}}
                                    onChange={(value)=>{onValueChange(element.privilege, value);}}
                                />
                            </Col>
                            <Col span={2} style={{textAlign:'right'}}>
                                {rowSet&&
                                <Button className="commission-clear-row-btn" onClick={()=>{clearRow(element.privilege);}}>
                                    &#10005;
                                </Button>}
                            </Col>
                        </Row>
                    );
                })}
                <Divider style={{margin:'12px 0'}}/>
                <Row align="middle" justify="space-between">
                    <Col>
                        {isDirty&&<Text style={{fontSize:13, color:'#8c8c8c'}}>Unsaved changes</Text>}
                    </Col>
                    <Col>
                        <Button
                            className="commission-plain-btn commission-footer-btn"
                            onClick={()=>{setCommission(savedCommission);}}
                            disabled={!isDirty}
                            style={{marginRight:8}}
                        >
                            Reset
                        </Button>
                        <Button
                            className="commission-update-btn"
                            onClick={handleUpdate}
                        >
                            Update
                        </Button>
                    </Col>
                </Row>
            </div>
        </div>
    )
}
